import { memo, useLayoutEffect, useRef } from 'react';

interface Props {
  peaks?: number[] | null;
  startSec?: number;
  durationSec?: number;
  volume?: number;
  color?: string;
  className?: string;
}

export const WaveformCanvas = memo(function WaveformCanvas({
  peaks,
  startSec = 0,
  durationSec,
  volume = 1.0,
  color = 'rgba(255, 255, 255, 0.55)',
  className,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const render = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Всегда берем строго актуальный физический размер канваса в DOM
    const rect = canvas.getBoundingClientRect();
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);

    if (w <= 0 || h <= 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;

    const targetW = Math.round(w * dpr);
    const targetH = Math.round(h * dpr);

    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    if (!peaks || peaks.length === 0) {
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.lineWidth = 1;
      ctx.moveTo(6, h / 2);
      ctx.lineTo(w - 6, h / 2);
      ctx.stroke();
      ctx.setLineDash([]);
      return;
    }

    const pts = 20; // 20 отсчетов в секунду (50 мс на точку)
    const totalLen = peaks.length;

    const sIdxExact = Math.max(0, startSec * pts);
    const dLenExact = durationSec !== undefined ? Math.max(0.01, durationSec * pts) : (totalLen - sIdxExact);
    const eIdxExact = Math.min(totalLen, sIdxExact + dLenExact);

    if (sIdxExact >= totalLen || eIdxExact <= sIdxExact) {
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.lineWidth = 1;
      ctx.moveTo(4, h / 2);
      ctx.lineTo(w - 4, h / 2);
      ctx.stroke();
      ctx.setLineDash([]);
      return;
    }

    const centerY = h / 2;
    const barW = 2;
    const gap = 1;
    const barPitch = barW + gap;
    const numBars = Math.max(1, Math.floor(w / barPitch));
    const step = dLenExact / numBars;
    const vol = Math.max(0, volume);
    const maxBarH = h - 4;

    ctx.fillStyle = color;
    let hasDrawn = false;

    // Бесшовный рендеринг без фазовых биений сетки и алиасинга
    for (let i = 0; i < numBars; i++) {
      const tStart = sIdxExact + i * step;
      const tEnd = tStart + step;
      let maxVal = 0;

      if (step >= 1.0) {
        // Обычный масштаб: непрерывное окно диапазона бара
        const idxStart = Math.max(0, Math.floor(tStart));
        const idxEnd = Math.min(totalLen, Math.max(idxStart + 1, Math.ceil(tEnd)));
        for (let j = idxStart; j < idxEnd; j++) {
          const v = peaks[j];
          if (v > maxVal) maxVal = v;
        }
      } else {
        // Глубокий зум (step < 1.0): субпиксельная линейная интерполяция
        const centerPos = tStart + step * 0.5;
        const idxLow = Math.max(0, Math.min(totalLen - 1, Math.floor(centerPos)));
        const idxHigh = Math.min(totalLen - 1, idxLow + 1);
        const frac = centerPos - idxLow;
        const v1 = peaks[idxLow] ?? 0;
        const v2 = peaks[idxHigh] ?? v1;
        maxVal = v1 + (v2 - v1) * frac;
      }

      if (maxVal > 0.001) {
        hasDrawn = true;
        const barH = Math.max(2, Math.min(1.0, maxVal * vol) * maxBarH);
        ctx.fillRect(i * barPitch, centerY - barH / 2, barW, barH);
      }
    }

    if (!hasDrawn) {
      ctx.beginPath();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.lineWidth = 1;
      ctx.moveTo(4, centerY);
      ctx.lineTo(w - 4, centerY);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  };

  const renderRef = useRef(render);
  renderRef.current = render;

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ro = new ResizeObserver(() => {
      renderRef.current();
    });
    ro.observe(canvas);
    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    render();
  }, [peaks, startSec, durationSec, volume, color]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    />
  );
});
